import React from "react";
import Box from "./Box";
import PropTypes from "prop-types";

/**
 * ResponsiveBox - A Box component with preset responsive properties for WCAG compliance
 * 
 * Automatically applies responsive width constraints that ensure content remains accessible
 * at 400% zoom (WCAG 2.1 Level AA requirement) while maintaining layout on larger screens.
 * 
 * Smart Defaults:
 * - Automatically detects Grid/HGrid children and applies appropriate container widths
 * - Detects form fields and applies form container patterns
 * - Converts fixed maxWidth values to responsive widths automatically
 * - Small screens (xs): 100% width when buttons/form fields are present
 * - maxWidth: 100% (always prevents overflow)
 * 
 * Optional flex layout support:
 * - Can enable responsive flex direction (column on mobile, row on desktop)
 * - Automatically adjusts gap spacing for mobile vs desktop
 * 
 * This component implements the common responsive pattern used throughout the application
 * to ensure WCAG compliance without affecting desktop layouts.
 * 
 * @component
 * @example
 * // Basic responsive width container
 * <ResponsiveBox>
 *   <Typography>Content that adapts to screen size</Typography>
 * </ResponsiveBox>
 * 
 * @example
 * // Smart: Automatically converts fixed width to responsive
 * <ResponsiveBox maxWidth="650px">
 *   <HGrid container>...</HGrid>
 * </ResponsiveBox>
 * // Becomes: width={{ xs: "100%", md: "650px" }}
 * 
 * @example
 * // With responsive flex layout
 * <ResponsiveBox flex>
 *   <TextField label="First Name" />
 *   <TextField label="Last Name" />
 * </ResponsiveBox>
 * 
 * @example
 * // With custom spacing and flex
 * <ResponsiveBox flex spacing={3} mt={2}>
 *   <Button>Cancel</Button>
 *   <Button>Submit</Button>
 * </ResponsiveBox>
 */
const ResponsiveBox = React.forwardRef(({ 
  children, 
  flex = false,
  width,
  maxWidth,
  spacing,
  sx,
  ...props 
}, ref) => {
  // Helper function to check if children contain buttons
  const hasButtonChildren = (children) => {
    if (!children) return false;
    
    // Handle arrays
    if (Array.isArray(children)) {
      return children.some(child => hasButtonChildren(child));
    }
    
    // Handle React elements
    if (React.isValidElement(children)) {
      const type = children.type;
      const typeName = typeof type === 'string' 
        ? type.toLowerCase() 
        : type?.displayName || type?.name || '';
      
      // Check if it's a button element or button component
      if (typeName === 'button' || 
          typeName.includes('button') || 
          typeName === 'erpsbasebutton') {
        return true;
      }
      
      // Recursively check children
      if (children.props?.children) {
        return hasButtonChildren(children.props.children);
      }
    }
    
    return false;
  };

  // Helper function to check if children contain Grid/HGrid
  const hasGridChildren = (children) => {
    if (!children) return false;
    
    // Handle arrays
    if (Array.isArray(children)) {
      return children.some(child => hasGridChildren(child));
    }
    
    // Handle React elements
    if (React.isValidElement(children)) {
      const type = children.type;
      const typeName = typeof type === 'string' 
        ? type.toLowerCase() 
        : type?.displayName || type?.name || '';
      
      // Check if it's a Grid or HGrid component
      if (typeName === 'grid' || 
          typeName === 'hgrid' ||
          (children.props?.container !== undefined) ||
          (children.props?.item !== undefined)) {
        return true;
      }
      
      // Recursively check children
      if (children.props?.children) {
        return hasGridChildren(children.props.children);
      }
    }
    
    return false;
  };

  // Helper function to check if children contain form fields
  const hasFormFieldChildren = (children) => {
    if (!children) return false;
    
    // Handle arrays
    if (Array.isArray(children)) {
      return children.some(child => hasFormFieldChildren(child));
    }
    
    // Handle React elements
    if (React.isValidElement(children)) {
      const type = children.type;
      const typeName = typeof type === 'string' 
        ? type.toLowerCase() 
        : type?.displayName || type?.name || '';
      
      // Check if it's a form field component
      if (typeName === 'input' ||
          typeName === 'textarea' ||
          typeName === 'select' ||
          typeName.includes('textfield') ||
          typeName === 'erpsbasetextfield' ||
          typeName === 'erpsbaseselect') {
        return true;
      }
      
      // Recursively check children
      if (children.props?.children) {
        return hasFormFieldChildren(children.props.children);
      }
    }
    
    return false;
  };

  // Convert fixed maxWidth to responsive width if provided
  // Common patterns: 500px, 650px, 800px, etc.
  const convertMaxWidthToResponsive = (maxWidthValue) => {
    if (!maxWidthValue || typeof maxWidthValue !== 'string') {
      return undefined;
    }
    
    // Extract numeric value if it's a pixel value
    const pixelMatch = maxWidthValue.match(/(\d+)px/);
    if (pixelMatch) {
      const pixelValue = parseInt(pixelMatch[1], 10);
      
      // Determine appropriate breakpoint based on width
      // Smaller widths (< 600px) use sm, medium (600-900px) use md, larger use lg
      let breakpoint = 'md'; // Default to md (900px)
      if (pixelValue < 600) {
        breakpoint = 'sm'; // 600px breakpoint
      } else if (pixelValue >= 900) {
        breakpoint = 'lg'; // 1200px breakpoint
      }
      
      return {
        xs: "100%",
        [breakpoint]: maxWidthValue
      };
    }
    
    // If it's already a percentage or other unit, use as-is but make responsive
    if (maxWidthValue.includes('%')) {
      return {
        xs: "100%",
        md: maxWidthValue
      };
    }
    
    return undefined;
  };

  // Determine default responsive width based on content and props
  let defaultWidth = undefined;
  
  // If maxWidth is provided as a fixed value, convert it to responsive
  const responsiveWidthFromMaxWidth = maxWidth ? convertMaxWidthToResponsive(maxWidth) : undefined;
  
  // Priority: explicit width > converted maxWidth > smart defaults
  if (width !== undefined) {
    // Use explicit width
    defaultWidth = width;
  } else if (responsiveWidthFromMaxWidth) {
    // Use converted maxWidth
    defaultWidth = responsiveWidthFromMaxWidth;
  } else {
    // Smart defaults based on content
    if (hasButtonChildren(children)) {
      defaultWidth = { xs: "100%" };
    } else if (hasGridChildren(children) || hasFormFieldChildren(children)) {
      // For Grid/Form containers, use a common pattern: 100% on mobile, constrained on desktop
      // This prevents overlap issues at intermediate screen sizes (like 600px)
      defaultWidth = { xs: "100%", md: "auto" };
    }
  }
  
  const responsiveWidth = defaultWidth;

  // Build base sx styles
  const baseSx = {
    maxWidth: "100%", // Always prevent overflow
  };
  
  // Only add width if responsiveWidth is defined
  if (responsiveWidth !== undefined) {
    baseSx.width = responsiveWidth;
  }
  
  // If maxWidth was provided and not converted to width, add it as maxWidth
  // (for cases where user wants both width and maxWidth constraints)
  if (maxWidth && !responsiveWidthFromMaxWidth) {
    baseSx.maxWidth = maxWidth;
  }

  // Add flex layout if requested
  if (flex) {
    baseSx.display = "flex";
    baseSx.flexDirection = { xs: "column", sm: "row" };
    
    // Add responsive gap if spacing is provided
    if (spacing !== undefined) {
      baseSx.gap = typeof spacing === 'number' 
        ? { xs: 2, sm: spacing } // Smaller gap on mobile (column), original on desktop (row)
        : spacing; // Use as-is if already responsive object
    }
  }

  // Merge with user-provided sx (user sx takes precedence)
  const mergedSx = sx 
    ? Array.isArray(sx) 
      ? [baseSx, ...sx]
      : { ...baseSx, ...sx }
    : baseSx;

  return (
    <Box ref={ref} sx={mergedSx} {...props}>
      {children}
    </Box>
  );
});

ResponsiveBox.displayName = 'ResponsiveBox';

ResponsiveBox.propTypes = {
  /**
   * The content of the component
   */
  children: PropTypes.node,
  /**
   * If true, enables responsive flex layout (column on mobile, row on desktop)
   * Applies: display: "flex", flexDirection: { xs: "column", sm: "row" }
   */
  flex: PropTypes.bool,
  /**
   * Responsive width values
   * Default: Smart defaults based on content (buttons, grids, form fields)
   * Set to null/undefined to use smart defaults, or override with custom responsive values
   * 
   * Smart defaults:
   * - Buttons: { xs: "100%" }
   * - Grids/Form fields: { xs: "100%", md: "auto" }
   * - Otherwise: undefined (natural width)
   */
  width: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      xs: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      sm: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      md: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      lg: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      xl: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ]),
  /**
   * Maximum width constraint
   * If provided as a fixed pixel value (e.g., "650px", "500px"), automatically converts to responsive width:
   * - { xs: "100%", md: "650px" } for 650px
   * - { xs: "100%", sm: "500px" } for 500px
   * 
   * This makes it easy to use fixed widths without breaking responsive behavior.
   * If you provide both width and maxWidth, width takes precedence.
   */
  maxWidth: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({
      xs: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      sm: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      md: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      lg: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      xl: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ]),
  /**
   * Gap spacing for flex layout (only applies when flex={true})
   * Can be a number (theme spacing multiplier) or object for responsive values
   * When using numeric spacing with flex, it's automatically adjusted:
   * - Mobile (xs): spacing={2} for reasonable vertical gaps
   * - Desktop (sm+): original spacing value for horizontal layout
   */
  spacing: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.shape({
      xs: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      sm: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      md: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      lg: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      xl: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ]),
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles
   * Will be merged with default responsive styles (user styles take precedence)
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])
    ),
    PropTypes.func,
    PropTypes.object,
  ]),
  /**
   * Margin top (theme spacing multiplier)
   */
  mt: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Margin right (theme spacing multiplier)
   */
  mr: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Margin bottom (theme spacing multiplier)
   */
  mb: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Margin left (theme spacing multiplier)
   */
  ml: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Margin x-axis (theme spacing multiplier)
   */
  mx: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Margin y-axis (theme spacing multiplier)
   */
  my: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Margin (theme spacing multiplier)
   */
  m: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Padding top (theme spacing multiplier)
   */
  pt: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Padding right (theme spacing multiplier)
   */
  pr: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Padding bottom (theme spacing multiplier)
   */
  pb: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Padding left (theme spacing multiplier)
   */
  pl: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Padding x-axis (theme spacing multiplier)
   */
  px: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Padding y-axis (theme spacing multiplier)
   */
  py: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Padding (theme spacing multiplier)
   */
  p: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * ARIA role for accessibility
   */
  role: PropTypes.string,
};

export default ResponsiveBox;
